/* Cookie / storage consent banner (SPA version).
   Reads and writes the same cookie the editorial site uses
   (`ash-cookie-consent`, scoped to .prajnainnovates.com) so a
   visitor who has already answered on one property is never
   asked again on the other. */

import { useEffect, useState } from 'react';

const COOKIE_NAME = 'ash-cookie-consent';
const LS_KEY = 'ash-cookie-consent:v1'; // legacy fallback
const TWO_YEARS_SECONDS = 60 * 60 * 24 * 730;

function apexDomain(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host.endsWith('prajnainnovates.com')) return '.prajnainnovates.com';
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq) === name) {
      return decodeURIComponent(part.slice(eq + 1));
    }
  }
  return null;
}

function writeCookie(name: string, value: string) {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${TWO_YEARS_SECONDS}`,
    'SameSite=Lax',
  ];
  const domain = apexDomain();
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
}

function currentChoice(): 'accepted' | 'declined' | 'blocked' | null {
  const fromCookie = readCookie(COOKIE_NAME);
  if (fromCookie === 'accepted' || fromCookie === 'declined') return fromCookie;
  /* Legacy localStorage fallback — migrate quietly to the shared cookie. */
  try {
    const legacy = localStorage.getItem(LS_KEY);
    if (legacy === 'accepted' || legacy === 'declined') {
      writeCookie(COOKIE_NAME, legacy);
      return legacy;
    }
  } catch {
    return 'blocked';
  }
  return null;
}

function persist(choice: 'accepted' | 'declined') {
  writeCookie(COOKIE_NAME, choice);
  try {
    localStorage.setItem(LS_KEY, choice);
  } catch {
    /* ignore */
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (currentChoice() !== null) return;
    const t = window.setTimeout(() => setVisible(true), 100);
    return () => window.clearTimeout(t);
  }, []);

  function dismiss(choice: 'accepted' | 'declined') {
    persist(choice);
    setLeaving(true);
    window.setTimeout(() => setVisible(false), 220);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      className={`fixed left-3 right-3 bottom-3 sm:left-5 sm:right-5 sm:bottom-5 z-[1000] mx-auto max-w-[640px] bg-paper border border-rule rounded-xl shadow-2xl transition-all duration-200 ${
        leaving ? 'opacity-0 translate-y-5' : 'opacity-100 translate-y-0'
      }`}
      style={{
        boxShadow:
          '0 24px 60px -20px rgba(19, 16, 12, 0.28), 0 8px 20px -6px rgba(19, 16, 12, 0.10)',
      }}
    >
      <div className="p-4 sm:p-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px] font-editorial text-[14px] leading-snug text-ink-soft">
          This site uses only strictly-necessary browser storage to keep you
          signed in and save your work. No analytics, no advertising, no
          tracking. See our{' '}
          <a
            href="https://prajnainnovates.com/privacy.html"
            target="_blank"
            rel="noopener"
            className="underline underline-offset-2 text-accent hover:text-ink"
          >
            privacy policy
          </a>{' '}
          for the full details.
        </div>
        <div className="flex gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => dismiss('declined')}
            className="font-label text-[11px] uppercase tracking-[0.1em] font-semibold px-3.5 py-2 rounded-full border border-rule bg-transparent text-ink-soft hover:text-ink hover:border-accent-soft transition-colors"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => dismiss('accepted')}
            className="font-label text-[11px] uppercase tracking-[0.1em] font-semibold px-3.5 py-2 rounded-full border bg-accent text-paper border-accent hover:bg-ink hover:border-ink transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
